
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserRole } from "@/types/roles";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface UserInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvited: () => void;
}

export const UserInviteDialog: React.FC<UserInviteDialogProps> = ({ 
  open, 
  onOpenChange,
  onInvited
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<UserRole>("user");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log("=== REAL INVITATION PROCESS STARTED ===");
    console.log("Input data:", { email: email.trim(), name: name.trim(), role });
    
    if (!email.trim()) {
      toast.error("Bitte geben Sie eine E-Mail-Adresse ein.");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log("Calling send-invitation Edge Function in REAL MODE...");
      
      const payload = { 
        email: email.trim(), 
        name: name.trim() || undefined, 
        role,
        inviter: user?.name || user?.email || "Administrator",
        realMode: true // Flag to ensure this is a real invitation
      };
      
      console.log("Payload for Edge Function:", payload);
      
      // Call the Edge Function
      const { data, error } = await supabase.functions.invoke("send-invitation", {
        body: payload
      });
      
      console.log("Edge Function Response:", { data, error });
      
      if (error) {
        console.error("Edge Function Error:", error);
        toast.error(`Fehler beim Senden der Einladung: ${error.message}`);
        return;
      }
      
      if (data?.success) {
        console.log("REAL INVITATION SENT SUCCESSFULLY!");
        toast.success(`Einladung an ${email.trim()} erfolgreich gesendet!`);
        
        // Reset form
        setEmail("");
        setName("");
        setRole("user");
        onOpenChange(false);
        onInvited();
      } else {
        const errorMessage = data?.message || "Unbekannter Fehler beim Senden der Einladung";
        console.error("Invitation failed:", errorMessage);
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Unexpected error during invitation:", error);
      toast.error(`Unerwarteter Fehler: ${error.message || "Einladung konnte nicht gesendet werden"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestInvitation = async () => {
    console.log("=== Loading test data ===");
    setEmail("smileboss98@gmail.com");
    setName("Björn Hoss");
    setRole("user");
    toast.info("Test-Daten eingefügt. Klicken Sie auf 'Einladung senden' zum Testen.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#10161D] border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white">
            Neuen Benutzer einladen
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Senden Sie eine ECHTE Einladung per E-Mail an einen neuen Benutzer.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid w-full gap-2">
            <Label htmlFor="email">E-Mail-Adresse *</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@beispiel.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#1A2433] border-gray-700 text-white"
              required
              disabled={isSubmitting}
            />
          </div>
          
          <div className="grid w-full gap-2">
            <Label htmlFor="name">Name (optional)</Label>
            <Input
              id="name"
              type="text"
              placeholder="Max Mustermann"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#1A2433] border-gray-700 text-white"
              disabled={isSubmitting}
            />
          </div>
          
          <div className="grid w-full gap-2">
            <Label htmlFor="role">Rolle</Label>
            <Select value={role} onValueChange={(value) => setRole(value as UserRole)} disabled={isSubmitting}>
              <SelectTrigger className="bg-[#1A2433] border-gray-700 text-white">
                <SelectValue placeholder="Wählen Sie eine Rolle" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A2433] border-gray-700 text-white">
                <SelectGroup>
                  <SelectLabel>Verfügbare Rollen</SelectLabel>
                  <SelectItem value="user" className="text-white hover:bg-[#2A3443]">Benutzer</SelectItem>
                  <SelectItem value="admin" className="text-white hover:bg-[#2A3443]">Administrator</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter className="mt-6 flex flex-col sm:flex-row gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleTestInvitation}
              className="border-blue-600 text-blue-300 hover:bg-blue-800 hover:text-white"
              disabled={isSubmitting}
            >
              Test-Daten laden
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button 
              type="submit"
              className="bg-[#41c7f0] text-white hover:bg-[#3ab1d9]"
              disabled={isSubmitting || !email.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Echte Einladung wird gesendet...
                </>
              ) : (
                'ECHTE Einladung senden'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
